import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTrackClickedTranscriptSearch } from "@air/analytics/src/events/useTrackClickedTranscriptSearch";
import { useTrackTranscriptSearchNextResultClicked } from "@air/analytics/src/events/useTrackTranscriptSearchNextResultClicked";
import { useTrackTranscriptSearchPreviousResultClicked } from "@air/analytics/src/events/useTrackTranscriptSearchPreviousResultClicked";
import { Close, Search } from "@air/next-icons";
import { IconButton } from "@air/primitive-icon-button";
import { Input } from "@air/primitive-input";
import { Spinner } from "@air/primitive-spinner";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useTranscriptControlsWidth } from "~/components/AssetModalV2/Content/Panel/Transcript/hooks/useTranscriptControlsWidth";
import { useTranscriptSearchHotkeys } from "~/components/AssetModalV2/Content/Panel/Transcript/hooks/useTranscriptSearchHotkeys";
import { TranscriptSearchControls } from "~/components/AssetModalV2/Content/Panel/Transcript/TranscriptSearchControls";
import { centralizedClipIdSelector } from "~/store/centralizedClip/selectors";
import { useAirStore } from "~/utils/ReduxUtils";
export const TranscriptSearch = /*#__PURE__*/ memo((param)=>{
    let { onSearch, onGoToNextResult, onGoToPrevResult, isLoading, totalResults } = param;
    const store = useAirStore();
    const inputRef = useRef(null);
    const controlsRef = useRef(null);
    const [currentResultIndex, setCurrentResultIndex] = useState(0);
    const [searchValue, setSearchValue] = useState("");
    const { trackClickedTranscriptSearch } = useTrackClickedTranscriptSearch();
    const { trackTranscriptSearchNextResultClicked } = useTrackTranscriptSearchNextResultClicked();
    const { trackTranscriptSearchPreviousResultClicked } = useTrackTranscriptSearchPreviousResultClicked();
    useEffect(()=>{
        setCurrentResultIndex(0);
    }, [
        searchValue
    ]);
    const [onDebouncedSearch] = useDebouncedCallback(onSearch, 300, {
        trailing: true
    });
    const { controlsWidth } = useTranscriptControlsWidth(controlsRef);
    const onValueChange = useCallback((newValue)=>{
        setSearchValue(newValue);
        onDebouncedSearch(newValue);
    }, [
        onDebouncedSearch
    ]);
    const _onSearchClear = useCallback(()=>{
        setSearchValue("");
        onSearch("");
    }, [
        onSearch
    ]);
    const setNextIndex = useCallback(()=>{
        if (!onGoToNextResult) return;
        const nextIndex = currentResultIndex < totalResults - 1 ? currentResultIndex + 1 : 0;
        setCurrentResultIndex(nextIndex);
        onGoToNextResult();
        trackTranscriptSearchNextResultClicked({
            clip_id: centralizedClipIdSelector(store.getState()),
            search_term: searchValue,
            new_index: nextIndex
        });
    }, [
        currentResultIndex,
        onGoToNextResult,
        searchValue,
        store,
        totalResults,
        trackTranscriptSearchNextResultClicked
    ]);
    const setPrevIndex = useCallback(()=>{
        if (!onGoToPrevResult) return;
        const prevIndex = currentResultIndex > 0 ? currentResultIndex - 1 : totalResults - 1;
        setCurrentResultIndex(prevIndex);
        onGoToPrevResult();
        trackTranscriptSearchPreviousResultClicked({
            clip_id: centralizedClipIdSelector(store.getState()),
            search_term: searchValue,
            new_index: prevIndex
        });
    }, [
        currentResultIndex,
        totalResults,
        onGoToPrevResult,
        trackTranscriptSearchPreviousResultClicked,
        store,
        searchValue
    ]);
    useTranscriptSearchHotkeys({
        inputRef,
        onSetNextIndex: onGoToNextResult ? setNextIndex : undefined,
        onSetPrevIndex: onGoToPrevResult ? setPrevIndex : undefined
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative z-1 w-full",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "absolute left-2 top-2 flex size-6 items-center justify-center",
                children: isLoading ? /*#__PURE__*/ _jsx(Spinner, {
                    className: "size-5 text-teal-9"
                }) : /*#__PURE__*/ _jsx(Search, {
                    className: "size-6 text-grey-10"
                })
            }),
            /*#__PURE__*/ _jsx(Input, {
                "data-testid": "TRANSCRIPT_SEARCH_INPUT",
                placeholder: "Search transcript",
                ref: inputRef,
                className: "w-full px-10 shadow-[0_0px_25px_20px_rgb(26,26,26)]",
                size: "large",
                style: {
                    paddingRight: controlsWidth + 12
                },
                value: searchValue,
                onFocus: ()=>{
                    const clipId = centralizedClipIdSelector(store.getState());
                    trackClickedTranscriptSearch({
                        clip_id: clipId
                    });
                },
                onChange: (ev)=>onValueChange(ev.target.value)
            }),
            /*#__PURE__*/ _jsxs("div", {
                ref: controlsRef,
                className: "absolute right-6 top-6 flex items-center gap-1",
                children: [
                    totalResults > 0 && !!searchValue && /*#__PURE__*/ _jsx(TranscriptSearchControls, {
                        totalResults: totalResults,
                        currentWordIndex: currentResultIndex,
                        onNextClick: onGoToNextResult ? setNextIndex : undefined,
                        onPrevClick: onGoToPrevResult ? setPrevIndex : undefined
                    }),
                    !!searchValue && /*#__PURE__*/ _jsx(IconButton, {
                        "data-testid": "TRANSCRIPT_SEARCH_CLEAR",
                        onClick: _onSearchClear,
                        icon: Close,
                        label: "Clear transcriopt search",
                        size: "small",
                        appearance: "ghost",
                        color: "grey"
                    })
                ]
            })
        ]
    });
});
TranscriptSearch.displayName = "TranscriptSearch";
